import * as Sentry from '@sentry/react';
import {IReactionDisposer, makeAutoObservable, reaction, runInAction} from 'mobx';
import {prodMode, storeVersion} from '../../config';
import {calculateAge} from '../../helpers/calculateAge';
import getField from '../../helpers/getField';
import {getStatesFromZip} from '../../helpers/getStateFromZip';
import {convertHeightToInches} from '../../helpers/heightToInches';
import {calculateBmiImperial} from '../../helpers/calculateBmiImperial';
import updateField from '../../helpers/updateField';
import {PROSPER_PARAMETERS} from '../constants';
import {type RootStore} from '../Store';
import {serializable} from '../sync/serialization';
import Phase1 from './phase1';
import Phase2 from './phase2';
import Phase3 from './phase3';
import Phase4 from './phase4';
export interface InitialProductRecommendation {
    initialProductRecommendation: {
        recommendedProductType: string;
        initialSelectedTab: string;
        resultBest: unknown;
        resultRetirement: unknown;
        resultTemporary: unknown;
        resultVul: unknown;
    };
}

export class UserData {
    parentStore: RootStore;

    @serializable()
    phase1: Phase1;

    @serializable()
    phase2: Phase2;

    @serializable()
    phase3: Phase3;

    @serializable()
    phase4: Phase4;

    @serializable()
    email = '';

    @serializable()
    firstName = '';

    @serializable()
    lastName = '';

    @serializable()
    phone = '';

    @serializable()
    consent = '';

    @serializable()
    consentAuthUrl = '';

    @serializable()
    consentTimestamp: number | null = null;

    @serializable()
    customerId = ''; // id returned from quote approval

    @serializable()
    startRef: string | null = null;

    @serializable()
    utm = {
        campaign: '',
        content: '',
        medium: '',
        source: '',
        term: '',
        origin: '',
        adgroup: '',
    };

    @serializable()
    referral = {
        rrWcid: '',
        rrWcidTtl: '',
        referralCode: '',
        partnerUserId: '',
        callbackUrl: '',
    };

    @serializable()
    partners = {
        tuneId: '',
        offerId: '',
        offerName: '',
        affiliateId: '',
        source: '',
    };

    @serializable()
    recommendedProductTypeMx = 'No recommendation made';

    @serializable()
    selectedProductTypeMx = 'No selection made';

    @serializable()
    recommendationOverridden = false;

    @serializable()
    wtStartTime: number | null = null;

    @serializable()
    wtLeadFormTime: number | null = null;

    @serializable()
    landingPage: string | null = null;

    @serializable()
    tuValidationStatus: 'PASS' | 'FAIL' | null = null;

    @serializable()
    tuFailureReasons: string[] = [];

    // Used for A/B/n testing analysis
    @serializable('experiment')
    private _experiment: Record<string, boolean> = {};

    private emailReactionDisposer: IReactionDisposer;

    constructor(parentStore: RootStore) {
        makeAutoObservable(this);
        this.parentStore = parentStore;

        this.phase1 = new Phase1();
        this.phase2 = new Phase2();
        this.phase3 = new Phase3(this);
        this.phase4 = new Phase4(this);

        this.emailReactionDisposer = reaction(
            () => this.email,
            (email) => {
                Sentry.setUser({email});
            },
        );
    }

    @serializable()
    get version() {
        return [prodMode ? 'prod' : 'staging', storeVersion].join('_');
    }

    @serializable()
    get uuid() {
        return this.parentStore.userId;
    }

    @serializable()
    get useProsperFlow(): boolean {
        return this.experiment.prosper3600 && this.prosperServeEligible && this.tuValidationStatus !== 'FAIL';
    }

    @serializable()
    get prosperServeEligible2(): string {
        return this.experiment.prosperServeEligibleTwo4355 ? 'Active' : 'Inactive';
    }

    @serializable()
    get prosperEligible(): boolean {
        const {dateOfBirth, zipCode, comfortableAmount, annualHouseholdIncomeNumeric} = this.phase1;
        const states = getStatesFromZip(+zipCode);
        const stateEligible = !(
            PROSPER_PARAMETERS.INELIGIBLE_STATES.includes(states.state) ||
            PROSPER_PARAMETERS.INELIGIBLE_STATES.includes(states.state2)
        );
        const age = calculateAge(new Date(dateOfBirth));
        return (
            age >= PROSPER_PARAMETERS.MIN_AGE &&
            age <= PROSPER_PARAMETERS.MAX_AGE &&
            comfortableAmount >= PROSPER_PARAMETERS.MIN_CONTRIBUTION &&
            stateEligible &&
            annualHouseholdIncomeNumeric >= PROSPER_PARAMETERS.MIN_HOUSEHOLD_INCOME
        );
    }

    // Leads coming from a B2B lead partner (utm_campaign contains 'B2BLeadPartner') are
    // handled by an agent-driven flow, so they are excluded from the Prosper recommendation
    // and from self-serve access to the customer portal.
    @serializable()
    get isB2BLeadPartner(): boolean {
        return (this.utm.campaign ?? '').toLowerCase().includes('b2bleadpartner');
    }

    @serializable()
    get prosperServeEligible(): boolean {
        const {dateOfBirth, zipCode, comfortableAmount, annualHouseholdIncomeNumeric} = this.phase1;
        const {height, weight, usedTobacco} = this.phase2;
        const states = getStatesFromZip(+zipCode);
        const stateEligible = !(
            PROSPER_PARAMETERS.INELIGIBLE_STATES.includes(states.state) ||
            PROSPER_PARAMETERS.INELIGIBLE_STATES.includes(states.state2)
        );
        const age = calculateAge(new Date(dateOfBirth));
        // calculate bmi
        const heightInInches = convertHeightToInches(height);
        if (Number.isNaN(heightInInches)) {
            return false;
        }
        if (Number.isNaN(weight) || weight === '') {
            return false;
        }
        const bmi = calculateBmiImperial(Number(weight), heightInInches);

        return (
            !this.isB2BLeadPartner &&
            age >= (this.experiment.prosperServeEligibleTwo4355 ? 18 : PROSPER_PARAMETERS.SERVE_MIN_AGE) &&
            age <= (this.experiment.prosperServeEligibleTwo4355 ? 50 : PROSPER_PARAMETERS.SERVE_MAX_AGE) &&
            comfortableAmount >= PROSPER_PARAMETERS.SERVE_MIN_CONTRIBUTION &&
            stateEligible &&
            bmi >= PROSPER_PARAMETERS.SERVE_MIN_BMI &&
            bmi <= PROSPER_PARAMETERS.SERVE_MAX_BMI &&
            annualHouseholdIncomeNumeric >= PROSPER_PARAMETERS.SERVE_MIN_HOUSEHOLD_INCOME
        );
    }

    /*@serializable()
    get prosperServeProsper(): boolean {
        return (
            this.prosperServeEligible &&
            this.experiment.prosperKo4305 &&
            this.phase2.prosperFlowChecklistFailed === 'No'
        );
    }*/

    @serializable()
    get showProsperFlowChecklist(): boolean {
        //return this.prosperServeEligible && this.experiment.prosper3600 && this.experiment.prosperKo4305;
        return false;
    }

    get experiment() {
        return this._experiment;
    }

    set experiment(flags: Record<string, boolean>) {
        this._experiment = flags;
    }

    resetPhase1() {
        this.phase1 = new Phase1();
    }

    resetPhase2() {
        this.phase2 = new Phase2();
    }

    resetPhase3() {
        this.phase3 = new Phase3(this);
    }

    async loadContactData(contactId: string): Promise<void> {
        try {
            const resp = await fetch(`/api/getContactData/${contactId}`);
            const contact = await resp.json();

            runInAction(() => {
                this.firstName = contact.firstName;
                this.lastName = contact.lastName;
                this.email = contact.email;
                this.phone = contact.phone;
            });
        } catch (e) {
            console.error('Could not fetch contact data', e);
        }
    }

    updateField = updateField.bind(this);
    getField = getField.bind(this);

    initAutoDefaultPages() {
        console.log('initAutoDefaultPages ' + true);
        const setVaue = localStorage.getItem('setDeaultValue');
        if (setVaue !== 'false') {
            this.phase1.updateField('military')(false);
            this.phase1.updateField('travellingOut')(false);
            this.phase1.updateField('usCitizen')(true);
            this.phase1.updateField('employmentStatus')('employed');
            this.phase2.updateField('aids')(false);
            localStorage.setItem('setDeaultValue', 'false');
        }
    }

    dispose() {
        this.emailReactionDisposer();
        this.phase3.dispose();
    }
}

export default UserData;
